
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { PermissionsService } from '@/services/permissions';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';

interface BulletinImageUploadProps {
  selectedImage: File | null;
  onImageSelect: (e: React.ChangeEvent<HTMLInputElement>, isMature?: boolean) => void;
  uploadProgress?: { progress: number; state: string };
}

const BulletinImageUpload = ({ 
  selectedImage, 
  onImageSelect,
  uploadProgress = { progress: 0, state: 'idle' }
}: BulletinImageUploadProps) => {
  const { user } = useAuth();
  const [isValidating, setIsValidating] = useState(false);
  const [allowMatureContent, setAllowMatureContent] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
  const MAX_FILENAME_LENGTH = 25; // Maximum characters to show before truncating

  // Check if user has verified age and enabled mature content
  useEffect(() => {
    import('@/services/mature-content-service').then(({ default: MatureContentService }) => {
      const shouldShow = MatureContentService.shouldShowMatureContent();
      setAllowMatureContent(shouldShow);
    });
  }, []);

  // Create preview URL when selectedImage changes
  useEffect(() => {
    if (selectedImage) {
      const url = URL.createObjectURL(selectedImage);
      setPreviewUrl(url);
      
      // Cleanup function to revoke the URL
      return () => {
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [selectedImage]);

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('is_admin, is_premium, age_verified_at, mature_content_enabled')
        .eq('id', user.id)
        .single();
      
      // Update mature content settings from profile data
      if (data?.age_verified_at || data?.mature_content_enabled) {
        import('@/services/mature-content-service').then(({ default: MatureContentService }) => {
          const settings = {
            ageVerified: !!data.age_verified_at,
            matureContentEnabled: !!data.mature_content_enabled
          };
          MatureContentService.setLocalSettings(settings);
          setAllowMatureContent(MatureContentService.shouldShowMatureContent());
        });
      }
      
      return data;
    },
    enabled: !!user?.id
  });

  const isPremiumUser = profile?.is_admin || profile?.is_premium;
  const acceptedTypes = PermissionsService.getAcceptedTypes(isPremiumUser);

  const truncateFilename = (filename: string) => {
    if (filename.length <= MAX_FILENAME_LENGTH) return filename;
    const extension = filename.split('.').pop();
    const name = filename.substring(0, filename.lastIndexOf('.'));
    return `${name.substring(0, MAX_FILENAME_LENGTH)}...${extension ? `.${extension}` : ''}`;
  };

  const isVideoFile = (file: File) => {
    return file.type.startsWith('video/') || 
           file.name.toLowerCase().endsWith('.mp4') || 
           file.name.toLowerCase().endsWith('.webm') || 
           file.name.toLowerCase().endsWith('.mov');
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    console.log('Selected file details:', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: new Date(file.lastModified).toISOString()
    });

    setIsValidating(true);
    try {
      if (!user?.id) {
        toast.error('Must be logged in to upload');
        e.target.value = '';
        setIsValidating(false);
        return;
      }
      
      // Basic validation without mature content check
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('File must be smaller than 25MB');
      }
      
      // Check if the file type is allowed
      const isAllowedType = acceptedTypes.split(',').some(type => {
        return file.type.includes(type.trim().replace('*', ''));
      });
      
      if (!isAllowedType) {
        throw new Error(`Invalid file type. Supported formats: ${
          isPremiumUser ? 'JPG, PNG, GIF, MP4' : 'JPG, PNG, HEIC'
        }`);
      }
      
      // Pass the file to parent component - mature content check happens after posting
      onImageSelect(e, false);
    } catch (error) {
      console.error('File validation error:', error);
      // Clear the input so the user can try again with a different file
      e.target.value = '';
      
      // Show error toast
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate file';
      toast.error(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        type="file"
        accept={acceptedTypes}
        onChange={handleFileSelect}
        className="hidden"
        id="image-upload"
        disabled={isValidating || uploadProgress.state === 'uploading'}
      />
      <label
        htmlFor="image-upload"
        className={cn(
          "cursor-pointer px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors",
          (isValidating || uploadProgress.state === 'uploading') && 'opacity-50 cursor-not-allowed'
        )}
      >
        {isValidating ? 'Validating...' : selectedImage ? 'Change Media' : 'Add Media'}
      </label>
      
      {selectedImage && previewUrl && (
        <div className="space-y-2">
          {/* Preview thumbnail */}
          <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-white/5 border border-white/10">
            {isVideoFile(selectedImage) ? (
              <video
                src={previewUrl}
                className="w-full h-full object-cover"
                muted
                preload="metadata"
              />
            ) : (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to filename display if image fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {/* File type indicator */}
            <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-xs rounded uppercase">
              {selectedImage.type.split('/')[1] || 'file'}
            </div>
          </div>
          
          {/* File info */}
          <div className="space-y-1">
            <span className="text-sm text-gray-400" title={selectedImage.name}>
              {truncateFilename(selectedImage.name)}
            </span>
            <div className="text-xs text-gray-500">
              {(selectedImage.size / (1024 * 1024)).toFixed(1)} MB
            </div>
            
            {uploadProgress.state === 'uploading' && (
              <Progress 
                value={uploadProgress.progress} 
                className="h-1 bg-white/10"
              />
            )}
          </div>
        </div>
      )}

      <div className="text-xs text-gray-400">
        Supported formats: {isPremiumUser ? 'JPG, PNG, HEIC, GIF, MP4' : 'JPG, PNG, HEIC'}
        {!isPremiumUser && (
          <span className="ml-1 text-primary">
            (Premium users can upload GIF and MP4)
          </span>
        )}
        
        {isValidating && (
          <div className="mt-1 flex items-center gap-1 text-blue-400">
            <Info className="h-3 w-3" />
            <span>Validating file type...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulletinImageUpload;
